
<template>
  <div class="recipe-form">
    <ez-image-upload
      :formKey="formKey"
      :previewUrl="draft.image || defaultImage"
      :isDefaultImage="!draft.image"
      columnMode
      name="image"
      @removeImage="onRemoveImage"
      @change="onUpdateImage"
    >
      <template #addImage>
        <span>Upload Item Image</span>
      </template>
      <template #removeImage>
        <font-awesome-icon icon="times" />
        <span>Remove Image</span>
      </template>
    </ez-image-upload>
    <ez-input
      :formKey="formKey"
      name="name"
      label="Item name*"
      :value="draft.name"
      @onChange="onNameChange"
      placeholder="Enter Item Name"
    ></ez-input>
    <ez-dropdown
      :formKey="formKey"
      name="type"
      label="Type"
      :selected="draft.type"
      @change="onTypeChange"
      :data="recipeTypes"
      isFullWidth
      placeholder="Please select item type"
    ></ez-dropdown>
    <v-select-search-unit
      ref="chooseUnit"
      :data="units"
      label="Unit*"
      placeholder="Choose Unit"
      name="unitId"
      search-property="label"
      has-actions
      @onEdit="editUnit"
      @onRemove="deleteUnit"
      @onAddNew="openUnitModal"
      @onSelect="selectUnit"
      :selected-id="draft.unitId"
    />
    <ez-mask-input
      v-if="isRecipe"
      ref="minQuantityDecimal"
      label="Quantity*"
      name="quantity"
      :form-key="formKey"
      :precision="2"
      class="mask-input mt-12"
      :value="draft.quantity || 0"
      type="input"
      :price-prefix="false"
      @input="onQuantityChange"
    />
    <ez-mask-input
      v-if="isMenuItem"
      :form-key="formKey"
      name="salesPrice"
      label="Sales Price*"
      class="mask-input mt-12"
      :value="draft.salesPrice"
      type="input"
      @input="onPriceChange"
    />
    <ez-mask-input
      :form-key="formKey"
      name="targetCogsAmount"
      label="Target COGS($)"
      class="mask-input mt-12"
      :value="draft.targetCogsAmount ?? null"
      type="input"
      :minValue="0"
      @input="onTargetCogsAmountChange"
    />
    <ez-mask-input
      v-if="isMenuItem"
      :form-key="formKey"
      name="targetCogsPercent"
      label="Target COGS(%)"
      class="mask-input mt-12"
      :value="draft.targetCogsPercent ?? null"
      type="input"
      :price-prefix="false"
      :precision="2"
      :minValue="0"
      :maxValue="100"
      customPrefix="%"
      @input="onTargetCogsPercentChange"
    />
    <div
      v-if="hasManageInventoryPermission && isInventoryEnabled"
      class="toggle-container mt-12">

      <div class="toggle-label">
        <span>Inventory Tracked</span>
      </div>
      <ez-on-off
        :formKey="formKey"
        :checked="draft.inventoryTracked || false"
        @change="onInventoryTrackedChange"
        name="inventoryTracked"
      />
    </div>
    <div class="tags mt-12">
      <hr />
      <p class="section-title">Add Tags</p>
      <span class="button-description">Create or Add Recipe Tag(s)</span>
      <ez-button
        v-if="!recipeTags.length"
        type="secondary"
        is-full-width
        @click="openTagsModal"
        :isLoading="isLoading"
      >
        <font-awesome-icon icon="plus" />
        Add Tags
      </ez-button>
      <ez-button
        v-else
        type="secondary"
        is-full-width
        @click="openTagsModal"
        class="height-fit-content"
        :isLoading="isLoading"
      >
        <div class="all-tags">
          <v-tag
            v-for="tag in recipeTags"
            :key="tag.id"
            :color="tag.color.colors[0]"
            :backgroundColor="tag.color.colors[1]"
            @click.stop="onTagClick(tag)"
            canRemove
          >
            {{ tag.name }}
          </v-tag>
        </div>
        <font-awesome-icon icon="pen" class="icon-absolute" />
      </ez-button>
    </div>
    <add-tags-modal
      ref="addTagsModal"
      @confirm="onAddConfirm"
      :tagType="TagType.Recipe"
    />
    <add-unit-modal
      ref="addUnitModal"
      :unit="unitToUpdate"
      @onClose="onUnitModalClose"
      @success="getUnits"
    />
    <remove-unit-modal
      ref="removeUnit"
      @onClose="onRemoveClose"
      @success="() => $emit('successDeleteUnit')"
      :unit="unitToDelete"
    />
  </div>
</template>

<script>
/**
 * SelectVenue
 * @version 1.0.0
 * @since 2.3.0
 */

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import EzDropdown from '@/components/ui/Dropdown';
import EzImageUpload from '@/components/ui/ImageUpload';
import EzInput from '@/components/ui/Input';
import EzOnOff from '@/components/ui/OnOff/EzOnOff';
import { getCurrency, clone } from '@/util/utils';
import { wizardListenerMixin } from '@/mixins/wizard';
import EzMaskInput from '@/components/ui/MaskInput/EzMaskInput';
import AddUnitModal from '@/components/v3/patterns/VUnitsSettings/AddUnitModal';
import RemoveUnitModal from '@/components/v3/patterns/VUnitsSettings/RemoveUnitModal';
import VSelectSearchUnit from '@/components/v3/patterns/VSelectSearchUnit';
import { LOADING_KEY, TagType } from '@/util/constants';
import VTag from '@/components/v3/elements/VTag';
import AddTagsModal from '@/components/v3/elements/VProductTagsSettings/AddTagsModal.vue';
import EzButton from '@/components/ui/Button';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzDropdown,
    EzImageUpload,
    EzInput,
    EzOnOff,
    EzMaskInput,
    AddUnitModal,
    RemoveUnitModal,
    VSelectSearchUnit,
    VTag,
    AddTagsModal,
    EzButton,
  },
  data() {
    return {
      recipeTypes: [],
      formKey: 'recipeForm',
      units: [],
      selectedUnit: {},
      unitToUpdate: {},
      unitToDelete: {},
      recipeTags: [],
      recipeTagsIds: [],
      TagType,
    };
  },
  computed: {
    ...mapState('entities/users', ['context', 'contextId', 'loggedUser']),
    ...mapState('recipes', ['draft']),
    ...mapState('tags', ['selectedTags', 'selectedTagsIds', 'allTags']),
    ...mapGetters('defaultImages', ['getDefaultImage']),
    ...mapGetters('loading', ['isSomeLoading']),
    defaultImage() {
      return this.getDefaultImage('category');
    },
    currencySymbol() {
      const { symbol } = getCurrency() || {};
      return symbol;
    },
    isRecipe() {
      return this.draft.type === 'recipe';
    },
    isMenuItem() {
      return this.draft.type === 'menu_item';
    },
    isInventoryEnabled() {
      return this.context?.features?.inventory || false;
    },
    hasManageInventoryPermission() {
      return this.$permission.has('manageInventory');
    },
    isLoading() {
      return this.isSomeLoading([LOADING_KEY.VENUE_FETCH_TAGS, LOADING_KEY.FETCH_TAG_COLORS]);
    },
    tags() {
      if (!this.recipeTagsIds.length) return { tagIds: '' };
      const tagIdsObject = {};
      this.recipeTagsIds.forEach((id, index) => {
        tagIdsObject[`tagIds[${index}]`] = id;
      });
      return tagIdsObject;
    },
  },
  methods: {
    ...mapMutations('recipes', ['CLEAR_DRAFT', 'UPDATE_DRAFT_RECIPE']),
    ...mapMutations('tags', ['REMOVE_SELECTED_TAG', 'SET_SELECTED_TAGS']),
    ...mapActions('entities/products', ['venueFetchUnits']),
    ...mapActions('recipes', ['venueFetchRecipeTypes']),
    ...mapActions('tags', ['venueFetchTags', 'fetchTagColors']),
    async getRecipeTypes() {
        const { data } = await this.venueFetchRecipeTypes();
        this.recipeTypes = data.data || [];

        if (this.draft.type && !this.draft.typeObject) {
          const typeObject = this.recipeTypes.find(type => type.id === this.draft.type);
          if (typeObject) {
            this.UPDATE_DRAFT_RECIPE({ typeObject });
          }
        }
    },

    async getUnits() {
      const {
        data: { data },
      } = await this.venueFetchUnits();
      this.units = data;
    },
    validateFirstStep() {
      if (!this.draft.name || this.draft.unitId === null || this.draft.unitId === undefined) {
        this.disableNextStep();
        return;
      }
      if (this.isRecipe && !this.draft.quantity) {
        this.disableNextStep();
        return;
      }
      if (this.isMenuItem && (this.draft.salesPrice === null || this.draft.salesPrice === undefined || this.draft.salesPrice === '')) {
        this.disableNextStep();
        return;
      }
      this.enableNextStep();
    },
    selectUnit(unit) {
      this.selectedUnit = unit;
      this.UPDATE_DRAFT_RECIPE({ unitId: unit.id, unit });
      this.validateFirstStep();
    },
    deleteUnit(unit) {
      this.unitToDelete = unit;
      this.$refs.removeUnit.open();
    },
    editUnit(unit) {
      if (!unit.owned) return;
      this.unitToUpdate = unit;
      this.openUnitModal();
    },
    openUnitModal() {
      this.$refs.addUnitModal.open();
    },
    onUnitModalClose() {
      this.unitToUpdate = {};
    },
    onRemoveClose() {
      if (this.selectedUnit?.id === this.unitToDelete?.id) {
        this.$refs.chooseUnit.reset();
        this.preSelectData(this.product);
      }
      this.unitToDelete = {};
    },
    onNameChange(value) {
      this.UPDATE_DRAFT_RECIPE({ name: value });
      this.validateFirstStep();
    },

    onTypeChange(value) {
      this.UPDATE_DRAFT_RECIPE({
        type: value.id,
        typeObject: value,
        items: [],
        salesPrice: value.id === 'menu_item' ? 0 : null,
        quantity: value.id === 'recipe' ? 1 : null,
        unitId: null,
        targetCogsAmount: null,
        targetCogsPercent: null,
      });
      this.$refs.chooseUnit.reset();
      this.validateFirstStep();
    },
    onInventoryTrackedChange(value) {
      this.UPDATE_DRAFT_RECIPE({ inventoryTracked: value });
      this.validateFirstStep();
    },
    onPriceChange(value) {
      this.UPDATE_DRAFT_RECIPE({ salesPrice: value });

      // Recalculate Target COGS(%) if Target COGS($) exists
      if (
        value &&
        value > 0 &&
        (this.draft.targetCogsAmount !== null && this.draft.targetCogsAmount !== undefined)
      ) {
        const amt = Number(this.draft.targetCogsAmount) || 0;
        const pct = parseFloat(((amt / value) * 100).toFixed(2)) || 0;
        this.UPDATE_DRAFT_RECIPE({ targetCogsPercent: pct });
      } else if (value === null || value === undefined || value === '' || value === 0) {
        // Clear percent when Sales Price is removed/zero
        this.UPDATE_DRAFT_RECIPE({ targetCogsPercent: null });
      }

      this.validateFirstStep();
    },
    onQuantityChange(value) {
      this.UPDATE_DRAFT_RECIPE({ quantity: value });
      this.validateFirstStep();
    },
    onTargetCogsAmountChange(value) {
      this.UPDATE_DRAFT_RECIPE({ targetCogsAmount: value });

      // Auto-calculate Target COGS(%) if conditions met
      if (
        this.isMenuItem &&
        this.draft.salesPrice &&
        this.draft.salesPrice > 0 &&
        value !== '' &&
        value !== null &&
        value !== undefined
      ) {
        const amt = Number(value) || 0;
        const pct = parseFloat(((amt / this.draft.salesPrice) * 100).toFixed(2)) || 0;
        this.UPDATE_DRAFT_RECIPE({ targetCogsPercent: pct });
      } else if (value === '' || value === null || value === undefined) {
        // Clear percent when amount is cleared
        this.UPDATE_DRAFT_RECIPE({ targetCogsPercent: null });
      }

      this.validateFirstStep();
    },
    onTargetCogsPercentChange(value) {
      this.UPDATE_DRAFT_RECIPE({ targetCogsPercent: value });

      // Auto-calculate Target COGS($) if conditions met
      if (
        this.isMenuItem &&
        this.draft.salesPrice &&
        this.draft.salesPrice > 0 &&
        value !== '' &&
        value !== null &&
        value !== undefined
      ) {
        const pct = Number(value) || 0;
        const amt = parseFloat((this.draft.salesPrice * (pct / 100)).toFixed(2)) || 0;
        this.UPDATE_DRAFT_RECIPE({ targetCogsAmount: amt });
      } else if (value === '' || value === null || value === undefined) {
        // Clear amount when percent is cleared
        this.UPDATE_DRAFT_RECIPE({ targetCogsAmount: null });
      }

      this.validateFirstStep();
    },
    onUpdateImage(image) {
      if (image) {
        this.UPDATE_DRAFT_RECIPE({image, imgModified: true});
        this.validateFirstStep();
      }
    },
    onRemoveImage() {
      this.UPDATE_DRAFT_RECIPE({ image: null, imgModified: true });
      this.validateFirstStep();
    },
    async openTagsModal() {
      await this.venueFetchTags({ query: { type: TagType.Recipe } });
      await this.fetchTagColors();
      this.SET_SELECTED_TAGS(this.recipeTagsIds);
      this.$refs.addTagsModal.open();
    },
    onTagClick(tag) {
      this.REMOVE_SELECTED_TAG(tag);
      this.recipeTagsIds = this.recipeTagsIds.filter(item => item !== tag.id);
      this.recipeTags = this.recipeTags.filter(item => item.id !== tag.id);
      this.UPDATE_DRAFT_RECIPE({ tagIds: this.recipeTagsIds });
    },
    onAddConfirm() {
      this.recipeTags = clone(this.selectedTags);
      this.recipeTagsIds = clone(this.selectedTagsIds);
      this.UPDATE_DRAFT_RECIPE({ tagIds: this.recipeTagsIds });
      this.$refs.addTagsModal.close();
    },
    async onNextStep() {
      this.$emit('stepCompleted');
    },
  },
  async mounted() {
    await this.getRecipeTypes();
    await this.getUnits();

    if (this.draft.tagIds?.length) {
      this.recipeTagsIds = this.draft.tagIds;
      this.recipeTags = this.allTags.filter(tag => this.recipeTagsIds.includes(tag.id));
    } else if (this.draft.tags) {
      this.recipeTags = clone(this.draft.tags);
      this.recipeTagsIds = this.draft.tags.map(item => item.id);
    }
    this.SET_SELECTED_TAGS(this.recipeTagsIds);

    this.validateFirstStep();
  },
};
</script>

<style lang="scss" scoped>
.recipe-form {
  margin: 2rem;
  .input-group {
    + .input-group {
      margin-top: 0.75rem;
    }
  }
  :deep() .ez-image-upload {
    margin-bottom: 1rem;
  }
}

.toggle-container {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .toggle-label {
    display: flex;
    flex-direction: column;

    span {
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 0.25rem;
    }

    small {
      font-size: 12px;
      color: #6C757D;
    }
  }
}

:deep() .mask-input .mask-input__input {
  font-weight: normal;
}

:deep() .select-search__wrapper {
  font-size: 14px;
}

.tags {
  hr {
    border: 0;
    border-top: 1px dashed #dee1e4;
    margin: 1.25rem 0;
  }

  .section-title {
    font-size: 14px;
    line-height: 22px;
    color: #252631;
    margin-bottom: 4px;
  }

  .button-description {
    color: #6c7995;
    font-size: 12px;
    line-height: 18px;
    display: block;
    margin-bottom: 6px;
  }
}

.all-tags {
  padding: 10px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.height-fit-content {
  height: auto;
  padding-right: 42px;
  position: relative;
}

.icon-absolute {
  position: absolute;
  top: 10px;
  right: 12px;
}
</style>
